import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import ObjectiveCard from "../components/ViewBoard/ObjectiveCard";
import TotalSVG from "../components/ViewBoard/TotalSVG";
import PasswordModal from './../components/PasswordModal/PasswordModal';
import "../styles/board.css";
import dayjs from "dayjs";
import Masonry from '@mui/lab/Masonry';
import { applyUserTheme } from '../theme/index.js';
import { useTranslation } from 'react-i18next';
import { checkBoardAccess, checkBoardPassword, getBoard } from "../api/boards";
import { updateInitiativeStatus, updateNumericProgress } from "../api/okr";
import {
  createObjectiveStatusComment,
  updateObjectiveStatusComment,
  createKeyResultStatusComment,
  updateKeyResultStatusComment,
  getObjectiveStatusComments,
  getKeyResultStatusComments,
} from "../api/statusComments";

const ViewBoard = () => {
    const { t } = useTranslation();
    const { company_slug, board_slug } = useParams();
    const [boardColor, setBoardColor] = useState(null);
    const [boardName, setBoardName] = useState("");
    const [statusCommentsEnabled, setStatusCommentsEnabled] = useState(false);
    const [boardAccessToken, setBoardAccessToken] = useState(null);
    const [objectives, setObjectives] = useState([]);
    const [totalProgress, setTotalProgress] = useState(0);
    const [totalProgressIncrease, setTotalProgressIncrease] = useState(0);
    const [expandAll, setExpandAll] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isManager, setIsManager] = useState(false);
    const [isViewer, setIsViewer] = useState(false);
    const [isKrChampion, setIsKrChampion] = useState(false);
    const [krChampionIds, setKrChampionIds] = useState([]);
    // Коментарі вимагають JWT (авторство прив'язане до user_id) — доступ через пароль борда
    // (без токена) не дозволяє редагувати коментарі, навіть якщо роль formально manager/admin.
    const [isNamedAccess, setIsNamedAccess] = useState(false);
    const [passwordError, setPasswordError] = useState("");
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const updateTimeout = useRef(null);

useEffect(() => {
  const loadBoardAccessAndTheme = async () => {
    try {
      // === 1) Тимчасовий кастиль кольору за company_slug ===
      let color = null;
      if (company_slug === 'endeavor') {
        color = '#09E1CD'; // важливо: з #09E1CD 0bb2a3
      }

      // якщо треба інші компанії — розкоментуй і розширюй
      // const hardcoded = { endeavor: '#09E1CD', acme: '#664499' };
      // color = hardcoded[company_slug] ?? color;

      // (опціонально) якщо немає кастилю — можеш лишити дефолт/тест:
      // if (!color) color = '#0F766E';

      if (color) {
        applyUserTheme(color, 'light');
        try { localStorage.setItem('okboard:userPrimaryColor', color); } catch {}
      } else {
        const root = document.documentElement;
        [
          '--primary-color',
          '--primary-color-66',
          '--primary-color-3a',
          '--primary-color-07',
          '--transparent-color',
          '--transparent-hover-color',
          '--border-color',
          '--secondary-color',
          '--transparent-color-absolute',
        ].forEach(v => root.style.removeProperty(v));
      }

      // === 2) Перевіряємо токен ===
      const token = localStorage.getItem('token');
      if (!token) {
        setShowPasswordModal(true);
        return;
      }

      // === 3) Перевірка доступу (іменний) ===
      const accessData = await checkBoardAccess(company_slug, board_slug);
      if (accessData.is_admin) {
        setIsNamedAccess(true);
        setIsAdmin(true);
      } else if (accessData.is_manager) {
        setIsNamedAccess(true);
        setIsManager(true);
      } else if (accessData.is_viewer) {
        setIsNamedAccess(true);
        setIsViewer(true);
      } else if (accessData.is_kr_champion) {
        setIsNamedAccess(true);
        setIsKrChampion(true);
        setKrChampionIds(accessData.kr_champion_ids || []);
      } else {
        setIsNamedAccess(false);
        // Немає іменного доступу — пропонуємо пароль
        setShowPasswordModal(true);
      }
    } catch (err) {
      console.error('Помилка ініціалізації сторінки борда:', err);
    }
  };

  loadBoardAccessAndTheme();

  // === 4) Анмаунт — повертаємо дефолтні кольори ===
  return () => {
    const root = document.documentElement;
    [
      '--primary-color',
      '--primary-color-66',
      '--primary-color-3a',
      '--primary-color-07',
      '--transparent-color',
      '--transparent-hover-color',
      '--border-color',
      '--secondary-color',
      '--transparent-color-absolute',
    ].forEach(v => root.style.removeProperty(v));
  };
}, [board_slug, company_slug]);

  const handlePasswordSubmit = async (enteredPassword) => {
    setIsLoading(true);
    try {
      const { ok, data } = await checkBoardPassword(company_slug, board_slug, enteredPassword);

      if (ok) {
        setBoardAccessToken(data.boardAccessToken || null);
        setIsManager(data.isManager);
        setIsViewer(data.isViewer);

        if (!data.isManager && !data.isViewer) {
          setPasswordError(t('view_board.wrong_password'));
        }
      } else {
        setPasswordError(t('view_board.server_error'));
      }
    } catch (err) {
      console.error("Помилка перевірки пароля:", err);
      setPasswordError(t('view_board.connection_error'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin || isManager || isViewer || isKrChampion) {
      const fetchBoardData = async () => {
        try {
          // const token = localStorage.getItem("token");
          // if (!token) {
          //   console.error("❌ Токен не знайдено!");
          //   return;
          // }

          const data = await getBoard(company_slug, board_slug, boardAccessToken);

          setBoardName(data.board_name || t('common.no_name'));
          setStatusCommentsEnabled(Boolean(data.status_comments_enabled));
          // setBoardSlug(data.board_slug);

          const { updatedObjectives, finalTotalProgress, totalIncrease } = calculateProgress(data.objectives);

          setObjectives(updatedObjectives);
          setTotalProgress(finalTotalProgress);
          setTotalProgressIncrease(totalIncrease);

          setShowPasswordModal(false);
        } catch (err) {
          console.error("Помилка завантаження борда:", err);
        }
      };
      fetchBoardData();
    }
  }, [company_slug, board_slug, isAdmin, isManager, isViewer, isKrChampion, boardAccessToken, t]);

const calculateProgress = (objectives, oneMonthAgo) => {
    let totalWeightedProgress = 0;
    let totalWeightedIncrease = 0;
    let totalBoardWeight = 0;

    const updatedObjectives = objectives.map((objective) => {
        let objectiveWeightedProgress = 0;
        let objectiveWeightSum = 0;
        let objectiveWeightedIncrease = 0;

        const updatedKeyResults = objective.key_results.map((kr) => {
          let krProgress = 0;
          let krIncrease = 0;
          if (kr.type === 'descriptive') {
              const totalInitWeight = kr.initiatives.reduce((sum, init) => sum + (init.weight || 1), 0);
              const completedWeight = kr.initiatives.reduce(
                (sum, init) => sum + ((init.is_completed ? init.weight || 1 : 0)), 0
              );
              const completedLastMonthWeight = kr.initiatives.reduce(
                (sum, init) => sum + ((init.is_completed && init.completed_at && dayjs(init.completed_at).isAfter(oneMonthAgo)) ? init.weight || 1 : 0),
                  0
              );

              krProgress = totalInitWeight > 0 ? (completedWeight / totalInitWeight) * 100 : 0;
              krIncrease = totalInitWeight > 0 ? (completedLastMonthWeight / totalInitWeight) * 100 : 0;
            } else if (kr.type === 'numeric') {
              const hasValidValues = kr.initial_value != null && kr.target_value != null && kr.current_progress != null;

                if (!hasValidValues) {
                  krProgress = 0;
                  krIncrease = 0;
                } else {
                  const initial = Number(kr.initial_value);
                  const target = Number(kr.target_value);
                  const current = Number(kr.current_progress);
                  const previous = Number(kr.previous_progress ?? kr.initial_value);

                  const range = Math.abs(target - initial);

                  krProgress = range !== 0
                    ? ((Math.abs(current - initial)) / range) * 100
                    : 0;

                  krProgress = current !== 0 ? Math.max(0, Math.min(100, krProgress)) : 0;

                  let previousKrProgress = range !== 0
                    ? ((Math.abs(previous - initial)) / range) * 100
                    : 0;

                  previousKrProgress = Math.max(0, Math.min(100, previousKrProgress));

                  krIncrease = krProgress - previousKrProgress;
                  krIncrease = Math.max(0, krIncrease);
                }
           }
            const krWeight = kr.weight || 0;
            objectiveWeightedProgress += krProgress * krWeight;
            objectiveWeightedIncrease += krIncrease * krWeight;
            objectiveWeightSum += krWeight;

            return { ...kr, progress: krProgress, increase: krIncrease };
        });

        const finalObjectiveProgress = objectiveWeightSum > 0 ? objectiveWeightedProgress / objectiveWeightSum : 0;
        const finalObjectiveIncrease = objectiveWeightSum > 0 ? objectiveWeightedIncrease / objectiveWeightSum : 0;
        const objectiveWeight = objective.weight || 0;

        totalWeightedProgress += finalObjectiveProgress * objectiveWeight;
        totalWeightedIncrease += finalObjectiveIncrease * objectiveWeight;
        totalBoardWeight += objectiveWeight;

        return {
            ...objective,
            key_results: updatedKeyResults,
            progress: finalObjectiveProgress,
            increase: finalObjectiveIncrease,
        };
    });

    const finalTotalProgress = totalBoardWeight > 0 ? totalWeightedProgress / totalBoardWeight : 0;
    const finalTotalIncrease = totalBoardWeight > 0 ? totalWeightedIncrease / totalBoardWeight : 0;

    return { updatedObjectives, finalTotalProgress, totalIncrease: finalTotalIncrease };
};

const toggleInitiativeCompletion = async (objectiveId, krId, initId, is_completedd, type) => {
    setObjectives((prevObjectives) => {
        const updatedObjectives = prevObjectives.map((objective) => {
            if (objective.id !== objectiveId) return objective;

            return {
                ...objective,
                key_results: objective.key_results.map((kr) => {
                    if (kr.id !== krId) return kr;

                    return {
                        ...kr,
                        initiatives: kr.initiatives.map((init) =>
                            init.id === initId
                                ? {
                                      ...init,
                                      is_completed: !init.is_completed,
                                      completed_at: !init.is_completed ? dayjs().format("YYYY-MM-DD") : null, // Зберігаємо дату виконання
                                  }
                                : init
                        ),
                    };
                }),
            };
        });
        if (type === "descriptive") {
          const { updatedObjectives: recalculatedObjectives, finalTotalProgress, totalIncrease } = calculateProgress(updatedObjectives);
          setObjectives(recalculatedObjectives);
          setTotalProgress(finalTotalProgress);
          setTotalProgressIncrease(totalIncrease);

          return recalculatedObjectives;
        } else {
          setObjectives(updatedObjectives);
          return updatedObjectives;
        }
    });

    // Відправка змін на бекенд
    try {
        await updateInitiativeStatus(objectiveId, krId, initId, is_completedd);
      } catch (err) {
        console.error("Помилка оновлення ініціативи:", err);
      }
    };

    const createObjectiveComment = async (objectiveId, text) => {
      try {
        const newComment = await createObjectiveStatusComment(objectiveId, text);
        setObjectives((prev) =>
          prev.map((o) => (o.id === objectiveId ? { ...o, latest_status_comment: newComment } : o))
        );
      } catch (err) {
        console.error("Помилка збереження коментаря обджектива:", err);
        throw err;
      }
    };

    const updateObjectiveComment = async (objectiveId, commentId, text) => {
      try {
        const updated = await updateObjectiveStatusComment(objectiveId, commentId, text);
        setObjectives((prev) =>
          prev.map((o) => (o.id === objectiveId ? { ...o, latest_status_comment: updated } : o))
        );
      } catch (err) {
        console.error("Помилка оновлення коментаря обджектива:", err);
        throw err;
      }
    };

    const createKrComment = async (krId, text) => {
      try {
        const newComment = await createKeyResultStatusComment(krId, text);
        setObjectives((prev) =>
          prev.map((o) => ({
            ...o,
            key_results: o.key_results.map((kr) =>
              kr.id === krId ? { ...kr, latest_status_comment: newComment } : kr
            ),
          }))
        );
      } catch (err) {
        console.error("Помилка збереження коментаря КР:", err);
        throw err;
      }
    };

    const updateKrComment = async (krId, commentId, text) => {
      try {
        const updated = await updateKeyResultStatusComment(krId, commentId, text);
        setObjectives((prev) =>
          prev.map((o) => ({
            ...o,
            key_results: o.key_results.map((kr) =>
              kr.id === krId ? { ...kr, latest_status_comment: updated } : kr
            ),
          }))
        );
      } catch (err) {
        console.error("Помилка оновлення коментаря КР:", err);
        throw err;
      }
    };

    // Функція для зміни стану розгортання всіх блоків
    const toggleAllExpand = () => {
        setExpandAll((prev) => !prev);
    };

    const updateNumericKRProgress = (krId, newProgress) => {
      // 1. миттєво оновлюємо локально значення слайдера
      setObjectives(prev =>
        prev.map(obj => ({
          ...obj,
          key_results: obj.key_results.map(kr => {
            if (kr.id !== krId) return kr;

            const { initial_value, target_value } = kr;
            const range = Math.abs(target_value - initial_value);
            const direction = target_value > initial_value ? 1 : -1;

            let progress = range !== 0
              ? ((newProgress - initial_value) * direction * 100) / range
              : 0;

            progress = Math.max(0, Math.min(100, progress));

            return {
              ...kr,
              progress,
              current_progress: newProgress
            };
          })
        }))
      );
    
      // 2. скидаємо попередній таймер, якщо є
      if (updateTimeout.current) {
        clearTimeout(updateTimeout.current);
      }

      // 3. Через 1 секунду — робимо повний перерахунок прогресу і надсилаємо значення
      updateTimeout.current = setTimeout(async () => {
        setObjectives(latestObjectives => {

          const { updatedObjectives, finalTotalProgress, totalIncrease } = calculateProgress(latestObjectives);

          setTotalProgress(finalTotalProgress);
          setTotalProgressIncrease(totalIncrease);

          // 4. Знаходимо kr і обчислюємо абсолютне значення
          const kr = updatedObjectives.flatMap(obj => obj.key_results).find(k => k.id === krId);

          if (kr) {
            const range = Math.abs(kr.target_value - kr.initial_value);
            const direction = kr.target_value > kr.initial_value ? 1 : -1;
            const absoluteValue = kr.initial_value + direction * (range * (kr.progress / 100));

            updateNumericProgress(krId, absoluteValue).catch(err => {
              console.error("Помилка надсилання numeric-прогресу:", err);
            });
          }

          return updatedObjectives;
        });
      }, 1000);
    }

    
    

  if (!isAdmin && !isManager && !isViewer && !isKrChampion) {
        return (
            <PasswordModal
                open={showPasswordModal}
                onSubmit={handlePasswordSubmit}
                onClose={() => setShowPasswordModal(false)}
                errorMessage={passwordError}
                isLoading={isLoading}
            />
        );
  }

  return (
    <div className="view-board-page okr">
      <div className="okr-top">
        <div className="okr-total">
          <div>
            <p className="ok-label-value">{boardName}</p>
            <p className="progress-increase">+{totalProgressIncrease.toFixed(0)}% {t('view_board.per_last_month')}</p>

          </div>
          <div className="okr-total-bottom">
            <svg xmlns="http://www.w3.org/2000/svg" width="46" height="27" viewBox="0 0 46 27" fill="none">
              <path d="M32.2 0L37.467 5.1525L26.243 16.1325L17.043 7.1325L0 23.8275L3.243 27L17.043 13.5L26.243 22.5L40.733 8.3475L46 13.5V0H32.2Z" fill="white"></path>
            </svg>
            <p id="total-progress">{totalProgress.toFixed(0)}%</p>
          </div>
        </div>
        <TotalSVG totalProgress={totalProgress} />
      </div>
      <div className="okr-buttons">
        <h2>{t('view_board.objectives')}</h2>
        <button className="button secondary-button" onClick={toggleAllExpand}>
            {expandAll ? t('view_board.collapse_all') : t('view_board.expand_all')}
        </button>
      </div>
      <Masonry columns={{ lg: 3, md: 2, sm: 1, xs: 1 }} spacing={3} sequential sx={{ width: "auto", marginTop: "10px" }}>
        {objectives.map((objective) => (
          <ObjectiveCard
            key={objective.id}
            objective={objective}
            toggleInitiativeCompletion={toggleInitiativeCompletion}
            expandAll={expandAll}
            isCheckBoxDisabled={isViewer}
            isKrChampion={isKrChampion}
            krChampionIds={krChampionIds}
            onNumericProgressChange={updateNumericKRProgress}
            isManager={isNamedAccess && isManager}
            isAdmin={isNamedAccess && isAdmin}
            onObjectiveCommentCreate={createObjectiveComment}
            onObjectiveCommentUpdate={updateObjectiveComment}
            onKrCommentCreate={createKrComment}
            onKrCommentUpdate={updateKrComment}
            onObjectiveCommentHistoryLoad={(objectiveId) => getObjectiveStatusComments(objectiveId, boardAccessToken)}
            onKrCommentHistoryLoad={(krId) => getKeyResultStatusComments(krId, boardAccessToken)}
            statusCommentsEnabled={statusCommentsEnabled}
          />
        ))}
      </Masonry>
    </div>
  );
};

export default ViewBoard;
